import { useMemo } from "react";

import { SINGLE_METRIC_KEYS, getSingleMetricConfig, type SingleMetricKey } from "../config/config";
import type { IUseMetricConfigurationParams, IMetricConfigurationResult } from "../types";

// =============================================================================
// DATA FETCHING HOOKS
// =============================================================================

// Re-export the existing data fetching hook for now
// This maintains compatibility while we consolidate


// =============================================================================
// CONFIGURATION HOOKS
// =============================================================================
// METRIC CONFIGURATION HOOKS
// =============================================================================

/**
 * Hook for managing metric configuration logic
 * Handles filtering, visibility, and disabled states for metrics
 */
const useMetricConfiguration = (params: IUseMetricConfigurationParams = {}): IMetricConfigurationResult => {
    const { duration, isWindow } = params;

    const isMetricDisabled = useMemo(() => {
        return (metricKey: SingleMetricKey): boolean => {
            const config = getSingleMetricConfig(metricKey);
            if (!config.isTabDisabled) {
                return false;
            }

            // Create durations object similar to what the original code expects
            const durations = {
                duration,
                forAPI: { isWindow },
            };

            return config.isTabDisabled(durations);
        };
    }, [duration, isWindow]);

    const isMetricVisible = useMemo(() => {
        return (metricKey: SingleMetricKey): boolean => {
            // For now, all metrics are visible unless disabled
            // This can be extended with more complex visibility logic if needed
            return !isMetricDisabled(metricKey);
        };
    }, [isMetricDisabled]);

    const availableMetrics = useMemo(() => {
        return SINGLE_METRIC_KEYS.filter(isMetricVisible);
    }, [isMetricVisible]);

    return {
        availableMetrics,
        getMetricConfig: getSingleMetricConfig,
        isMetricDisabled,
        isMetricVisible,
    };
};

/**
 * Helper hook for getting enriched metric data for tabs
 * This replicates the enrichmentTabs logic from the original Tabs component
 */
export const useEnrichedMetrics = (
    enrichmentData: Record<string, number> | undefined,
    params: IUseMetricConfigurationParams = {},
) => {
    const { availableMetrics, getMetricConfig, isMetricDisabled } = useMetricConfiguration(params);

    const enrichedMetrics = useMemo(() => {
        return availableMetrics.map((metricKey) => {
            const config = getMetricConfig(metricKey);
            // Use string ID directly to access enrichmentData - much cleaner!
            const value = enrichmentData?.[config.id] ?? 0;

            return {
                ...config,
                key: metricKey,
                value,
                formattedValue: config.formatter(value),
                isDisabled: isMetricDisabled(metricKey),
            };
        });
    }, [availableMetrics, getMetricConfig, enrichmentData, isMetricDisabled]);

    return {
        enrichedMetrics,
        availableMetrics,
        getMetricConfig,
    };
};
